import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Grid, TextField, RadioGroup, FormControlLabel, Radio, Typography, Stack, Button, Alert, Slide, Divider, CircularProgress } from '@mui/material';
import { saveSelfProbationReview, getProbationReview } from './ProbationReviewQueries';
import DynamicFormRenderer from './DynamicFormRenderer';

const ratingOptions = ['Needs Improvement', 'Meets Expectations', 'Exceeds Expectations'];

const unwrap = (v) => (v && typeof v === 'object' && 'S' in v ? v.S : v);
const safeJSON = (v) => {
  const raw = unwrap(v);
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};
const getEmailFromLocalStorage = () => {
  const raw = localStorage.getItem('user');
  if (!raw) return '';
  try {
    const obj = JSON.parse(raw);
    return unwrap(obj.email || (obj.user && obj.user.email) || obj.user_email || obj.employee_email) || '';
  } catch { return raw.includes('@') ? raw : ''; }
};

const hasManagerResponse = (resp) => {
  if (!resp || typeof resp !== 'object') return false;
  
  // Check for new schema field names (manager_test_case_design, manager_manual_testing, etc.)
  const newSchemaKeys = [
    'manager_test_case_design', 
    'manager_manual_testing', 
    'manager_product_knowledge', 
    'manager_dependability', 
    'manager_productivity', 
    'manager_teamwork', 
    'overall_performance', 
    'probation_progress',
    'manager_comments'
  ];
  
  // Check for old schema field names (testCaseDesign, manualTesting, etc.)
  const oldSchemaKeys = [
    'testCaseDesign', 
    'manualTesting', 
    'productKnowledge', 
    'dependability', 
    'productivity', 
    'teamwork', 
    'overallPerformance', 
    'probationProgress'
  ];
  
  // Check both new and old schema field names
  const allKeys = [...newSchemaKeys, ...oldSchemaKeys];
  return allKeys.some(k => {
    const value = resp[k];
    return value && String(value).trim() !== '' && String(value).trim() !== 'undefined';
  });
};

const SelfProbationForm = ({ details, reviewsCache = [], refreshCache, formSchema }) => {
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, severity: 'success', message: '' });
  const [existing, setExisting] = useState(null);
  const [managerReview, setManagerReview] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);
  const [isPeriodActive, setIsPeriodActive] = useState(true);

  const userEmail = useMemo(() => String(getEmailFromLocalStorage() || '').toLowerCase(), []);
  const draftKey = useMemo(() => `selfProbationDraft_${userEmail}_${details.periodStart}`, [userEmail, details.periodStart]);

  // Check if current period is active based on form dates
  useEffect(() => {
    if (!details.periodStart || !details.periodEnd) {
      setIsPeriodActive(true); // Default to active if no dates provided
      return;
    }

    const now = new Date();
    const start = new Date(details.periodStart);
    const end = new Date(details.periodEnd);
    const isActive = now >= start && now <= end;
    
    setIsPeriodActive(isActive);
    setActivePeriod(isActive ? { period_start: details.periodStart, period_end: details.periodEnd } : null);
  }, [details.periodStart, details.periodEnd]);

  // Load existing submitted review or draft on mount
  useEffect(() => {
    const loadReview = async () => {
      if (!userEmail || !details.periodStart) return;
      
      try {
        // Construct the review ID based on email and period start
        const reviewId = `${userEmail}_probation_${details.periodStart}`;
        
        // Fetch the review from the database
        const review = await getProbationReview(userEmail, reviewId);
        
        console.log('[SelfProbationForm] Loaded review:', review);
        
        if (review && review.review) {
          const userResp = safeJSON(review.review.user_response) || review.review.user_response || null;
          
          // Extract manager response - need to unwrap first
          const mgrRespRaw = unwrap(review.review.manager_response);
          console.log('[SelfProbationForm] Manager response raw:', mgrRespRaw);
          const mgrResp = safeJSON(mgrRespRaw) || mgrRespRaw || null;
          console.log('[SelfProbationForm] Manager response parsed:', mgrResp);
          
          // Set manager review data - check if it has actual response data
          if (mgrResp && typeof mgrResp === 'object') {
            // The structure is: { schema: [...], responses: { manager_test_case_design: "...", ... }, submittedAt, submittedBy }
            // Extract the responses object
            let actualResponses = mgrResp.responses || mgrResp;
            
            // If responses is a string or wrapped, unwrap it
            if (typeof actualResponses === 'string') {
              try {
                actualResponses = JSON.parse(actualResponses);
              } catch (e) {
                console.error('[SelfProbationForm] Failed to parse responses string:', e);
              }
            }
            
            // Unwrap DynamoDB format for nested objects
            if (actualResponses && typeof actualResponses === 'object') {
              const cleaned = {};
              for (const key in actualResponses) {
                if (key === 'S' && actualResponses[key] === '') continue; // Skip empty S property
                cleaned[key] = unwrap(actualResponses[key]);
              }
              actualResponses = cleaned;
            }
            
            console.log('[SelfProbationForm] Actual responses after cleaning:', actualResponses);
            console.log('[SelfProbationForm] Has manager response?', hasManagerResponse(actualResponses));
            
            // Use hasManagerResponse to check if there's actual data
            if (hasManagerResponse(actualResponses)) {
              setManagerReview(actualResponses);
              console.log('[SelfProbationForm] Manager review set with data');
            } else {
              setManagerReview(null);
              console.log('[SelfProbationForm] Manager response has no valid data');
            }
          } else {
            setManagerReview(null);
            console.log('[SelfProbationForm] No manager response');
          }
          
          if (userResp) {
            // Check if response is in structured format (with schema and responses)
            if (userResp.schema && userResp.responses) {
              setExisting(userResp.responses);
            } else {
              setExisting(userResp);
            }
            setInputs({}); // Clear inputs when showing existing
            localStorage.removeItem(draftKey);
            return;
          }
        }
        
        // If no submitted review found, clear existing state
        setExisting(null);
        setManagerReview(null);
        
        // Load draft from localStorage
        const draft = localStorage.getItem(draftKey);
        if (draft) {
          const parsed = JSON.parse(draft);
          setInputs(parsed);
        } else {
          setInputs({});
        }
      } catch (e) {
        console.error('Failed to load review:', e);
        setExisting(null);
        setManagerReview(null);
        setInputs({});
      }
    };
    
    loadReview();
  }, [userEmail, draftKey, details.periodStart]);
  
  // Also listen to reviewsCache updates to refresh if needed
  useEffect(() => {
    if (!userEmail || !Array.isArray(reviewsCache) || reviewsCache.length === 0) return;
    
    const match = reviewsCache.find((it) => {
      const v = unwrap(it?.email);
      const periodStart = unwrap(it?.period_start);
      return String(v || '').toLowerCase() === userEmail && periodStart === details.periodStart;
    });
    
    if (match) {
      const userResp = safeJSON(match.user_response) || match.user_response || null;
      
      // Extract manager response - need to unwrap first
      const mgrRespRaw = unwrap(match.manager_response);
      const mgrResp = safeJSON(mgrRespRaw) || mgrRespRaw || null;
      
      // Set manager review data - check if it has actual response data
      if (mgrResp && typeof mgrResp === 'object') {
        // The structure is: { schema: [...], responses: { manager_test_case_design: "...", ... }, submittedAt, submittedBy }
        // Extract the responses object
        let actualResponses = mgrResp.responses || mgrResp;
        
        // Clean up any DynamoDB artifacts (like {S: ""} property in responses)
        if (actualResponses && typeof actualResponses === 'object') {
          const cleaned = { ...actualResponses };
          delete cleaned.S; // Remove DynamoDB artifact if present
          actualResponses = cleaned;
        }
        
        // Use hasManagerResponse to check if there's actual data
        if (hasManagerResponse(actualResponses)) {
          setManagerReview(actualResponses);
        } else {
          setManagerReview(null);
        }
      } else {
        setManagerReview(null);
      }
      
      if (userResp) {
        // Check if response is in structured format (with schema and responses)
        if (userResp.schema && userResp.responses) {
          setExisting(userResp.responses);
        } else {
          setExisting(userResp);
        }
        setInputs({}); // Clear inputs when showing existing
      }
    }
  }, [reviewsCache, userEmail, details.periodStart]);

  // Auto-save to localStorage with debouncing
  useEffect(() => {
    if (!userEmail || existing || !isPeriodActive) return;
    
    const timeoutId = setTimeout(() => {
      const hasContent = Object.values(inputs).some(v => String(v || '').trim() !== '');
      if (hasContent) {
        try {
          localStorage.setItem(draftKey, JSON.stringify(inputs));
          console.log('Self probation review draft auto-saved');
        } catch (e) {
          console.error('Failed to save draft:', e);
        }
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [inputs, userEmail, draftKey, existing, isPeriodActive]);

  const isValid = useMemo(() => {
    // When using dynamic form renderer with schema
    if (formSchema) {
      const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
      const employeeSections = schema.sections?.filter(s => s.completedBy === 'employee') || [];
      
      // Check all required fields are filled
      for (const section of employeeSections) {
        for (const field of section.fields || []) {
          if (field.required && field.fieldType !== 'heading') {
            const value = inputs[field.fieldId];
            if (!value || String(value).trim() === '') {
              return false;
            }
          }
        }
      }
      return true;
    }
    
    // Legacy static form validation
    return (
      String(inputs.testCaseDesign || '').trim() !== '' &&
      String(inputs.manualTesting || '').trim() !== '' &&
      String(inputs.productKnowledge || '').trim() !== '' &&
      String(inputs.dependability || '').trim() !== '' &&
      String(inputs.productivity || '').trim() !== '' &&
      String(inputs.teamwork || '').trim() !== '' &&
      String(inputs.improvements || '').trim() !== ''
    );
  }, [inputs, formSchema]);

  const handleChange = (e) => {
    setInputs(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPeriodActive) {
      setAlert({ open: true, severity: 'error', message: 'Cannot submit review outside the active period.' });
      return;
    }

    setLoading(true);
    try {
      // Prepare response with schema structure
      let responseData = { ...inputs };
      
      // If using dynamic form with schema, include the schema with responses
      if (formSchema) {
        const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
        const employeeSections = schema.sections?.filter(s => s.completedBy === 'employee') || [];
        
        // Create structured response with questions and answers
        const structuredResponse = {
          schema: employeeSections,
          responses: inputs,
          submittedAt: new Date().toISOString()
        };
        
        responseData = structuredResponse;
      }
      
      await saveSelfProbationReview({ 
        responseData,
        ...(details || {}), 
        userEmail,
        periodStart: details.periodStart,
        periodEnd: details.periodEnd,
        reviewPeriod: details.reviewPeriod,
        formId: details.formId || ''
      });
      localStorage.removeItem(draftKey);
      setAlert({ open: true, severity: 'success', message: 'Employee Self-Evaluation submitted successfully.' });
      
      // Refresh cache to reload the submitted data
      if (refreshCache) {
        await refreshCache();
      }
      
      // Set existing from inputs to show read-only view immediately
      setExisting(inputs);
    } catch (err) {
      setAlert({ open: true, severity: 'error', message: 'Failed to submit Employee Self-Evaluation.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Paper elevation={2} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Employee Self-Evaluation</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>To be completed by the employee and shared with the manager prior to the review discussion</Typography>
      {alert.open && (
        <Slide in={alert.open} direction="down">
          <Alert severity={alert.severity} onClose={() => setAlert(a => ({ ...a, open: false }))}>{alert.message}</Alert>
        </Slide>
      )}
      
      {!isPeriodActive && !existing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This review period is not currently active. You can only fill the form during the active period.
        </Alert>
      )}

      {existing ? (
        <Box>
          <Divider sx={{ mb: 2 }} />
          {formSchema ? (
            // Show read-only dynamic form when schema is provided
            <DynamicFormRenderer
              formSchema={formSchema}
              formData={existing}
              setFormData={() => {}}
              sectionFilter="employee"
              readOnly={true}
            />
          ) : (
            // Fallback to static display
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Test Case Design:</strong> {existing.testCaseDesign || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Manual Testing:</strong> {existing.manualTesting || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Product Knowledge:</strong> {existing.productKnowledge || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Dependability:</strong> {existing.dependability || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Productivity:</strong> {existing.productivity || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Teamwork:</strong> {existing.teamwork || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Recommended Improvements:</strong> {existing.improvements || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          {formSchema ? (
            // Use dynamic form renderer when schema is provided
            <DynamicFormRenderer
              formSchema={formSchema}
              formData={inputs}
              setFormData={setInputs}
              sectionFilter="employee"
            />
          ) : (
            // Fallback to static form
            <>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 3 }}>
                To be completed by the employee and shared with the manager prior to the review discussion.
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    1. How effectively did you design clear, structured, and reusable test cases based on requirements, user stories, and acceptance criteria?
                  </Typography>
                  <RadioGroup name="testCaseDesign" value={inputs.testCaseDesign} onChange={handleChange} row>
                    {ratingOptions.map(opt => (
                      <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />
                    ))}
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    2. How effectively did you perform manual testing and identify defects with clear reproduction steps and supporting evidence?
                  </Typography>
                  <RadioGroup name="manualTesting" value={inputs.manualTesting} onChange={handleChange} row>
                    {ratingOptions.map(opt => (
                      <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />
                    ))}
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    3. How effectively did you improve your understanding of the product, testing tools, QA processes, and the mortgage domain during this period?
                  </Typography>
                  <RadioGroup name="productKnowledge" value={inputs.productKnowledge} onChange={handleChange} row>
                    {ratingOptions.map(opt => (
                      <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />
                    ))}
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>4. How would you rate yourself on these attributes?</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Dependability</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Reliable, steady, and trustworthy. People can count on you to get work done on time and with good quality.
                  </Typography>
                  <RadioGroup name="dependability" value={inputs.dependability} onChange={handleChange} row>
                    {ratingOptions.map(opt => (
                      <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />
                    ))}
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Productivity</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    You perform at the highest level of your abilities and use your skills and resources efficiently to achieve the best possible outcome.
                  </Typography>
                  <RadioGroup name="productivity" value={inputs.productivity} onChange={handleChange} row>
                    {ratingOptions.map(opt => (
                      <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />
                    ))}
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Teamwork</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Collaborative and communicative. Others can count on you to work together to successfully complete tasks and help create a positive work environment.
                  </Typography>
                  <RadioGroup name="teamwork" value={inputs.teamwork} onChange={handleChange} row>
                    {ratingOptions.map(opt => (
                      <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />
                    ))}
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    5. Based on your experience during this period, what improvements would you recommend to help the team or organization work more effectively?
                  </Typography>
                  <TextField 
                    required 
                    label="Recommended Improvements" 
                    name="improvements" 
                    value={inputs.improvements} 
                    onChange={handleChange} 
                    fullWidth 
                    multiline 
                    rows={4}
                    disabled={!isPeriodActive}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" disabled={loading || !isPeriodActive}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={loading || !isValid || !isPeriodActive} 
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Submit Self Review
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
    </>
  );
};

export default SelfProbationForm;
